/****************************************************************
 * File: page-layout.tsx
 * Description: 页面公用的布局
 * Author: zayfen <zhangyunfeng@pudutech.com>
 * Created: 2022/05/25 17:29
 *
 * History:
 *   Date               Programmer ID    Description
 *  -----------------  ---------------  ------------------------
 *  2022/05/25 17:29   zayfen           Init
 *
 * This unpublished material is proprietary to PuduTech.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of PuduTech.
 *****************************************************************/

import { NextPage } from 'next'
import { useRouter } from 'next/router'

import { PropsWithChildren, useEffect, createRef } from 'react'
import PdFooter from '../pd-footer/pd-footer'
import PdNavbar from '../pd-navbar/pd-navbar'
import { MainContainerProvider } from '../../hooks/use-main-container'

export interface PageLayoutProps {
  sticky?: boolean
  bgWhite?: boolean
  navbarMoveById?: string
  pageTitle: string
  pageDescription: string
  pageKeywrods: string
  ogtype: 'website' | 'article'
  jumpRefresh?: boolean
  hideChangeLang?: boolean
  onScrollCapture?: (prevScrollTop: number, currentScrollTop: number, navbar: HTMLElement, moveBy: HTMLElement) => void
  containerClassName?: string
}

const PageLayout: NextPage<PropsWithChildren<PageLayoutProps>> = ({
  sticky,
  bgWhite,
  navbarMoveById,
  // pageTitle, // 移除：SEO 由 _app.tsx 统一处理
  // pageDescription, // 移除
  // pageKeywrods, // 移除
  // ogtype, // 移除
  onScrollCapture,
  children,
  jumpRefresh,
  hideChangeLang,
  containerClassName,
}) => {
  const router = useRouter()
  const mainRef: any = createRef()

  useEffect(() => {
    const callbacks: Array<() => void> = []

    let moveBy: HTMLElement | null = document.getElementById('' + navbarMoveById)
    const moveByVisible: boolean = !!moveBy && moveBy?.offsetWidth > 0

    // console.log('moveByVisible: ', moveByVisible)
    // console.log('moveByVisible: offsetHeight: ', moveBy?.offsetHeight)

    if (navbarMoveById && moveByVisible) {
      callbacks.push(registerAutoMoveNavbar())
    }

    const navbar = document.getElementById('pdNavbar')
    if (navbar !== null) {
      navbar.style.transform = 'translateY(0)'
    }

    return () => {
      callbacks.forEach((callback) => callback())
    }
  }, [router])

  function registerAutoMoveNavbar(): () => void {
    /* 上一次的scrollTop */
    let prevScrollTop = 0

    /* 使navbar随着main滚动 */

    const scrollHandler = (evt: Event) => {
      let navbar: HTMLDivElement | null = document.querySelector('#pdNavbar')
      let moveBy: HTMLElement | null = document.getElementById('' + navbarMoveById)

      const target = evt.target as HTMLDivElement

      if (
        target === null ||
        moveBy === null ||
        navbar === null ||
        moveBy.clientWidth === 0 ||
        moveBy.clientHeight === 0
      ) {
        console.log('Dont move navbar')
        if (navbar) {
          navbar.style.transform = `translateY(0)`
        }

        return
      }

      const maxNavbarOffsetHeight = navbar.offsetHeight

      const moveByOffsetTop = Math.max(moveBy.offsetTop, 0)

      const moveByViewportTop = moveBy.offsetTop - target.scrollTop

      const navbarMoveUp = Math.min(moveByViewportTop - maxNavbarOffsetHeight, 0)

      let translateY = Math.max(navbarMoveUp, -maxNavbarOffsetHeight)

      // console.log('Move navbar: ', translateY)
      navbar.style.transform = `translateY(${translateY}px)`

      /* if page moving up, then show navbar */
      onScrollCapture?.(prevScrollTop, target.scrollTop, navbar, moveBy)

      prevScrollTop = target.scrollTop
    }

    /* register */
    document.getElementById('main')?.addEventListener('scroll', scrollHandler, { passive: true })

    return () => {
      document.getElementById('main')?.removeEventListener('scroll', scrollHandler)
    }
  }

  return (
    <div className={['container-fluid w-full h-full', containerClassName].join(' ')}>
      {/* SEO 已由 _app.tsx 统一接管，此处不再渲染 Head/PdSEO */}

      <main
        id="main"
        ref={mainRef}
        className={['w-full h-screen overflow-y-auto will-change-scroll', bgWhite ? 'bg-white' : 'bg-pdbg'].join(' ')}
      >
        <MainContainerProvider value={mainRef}>
          <PdNavbar sticky={sticky} id="pdNavbar" jumpRefresh={jumpRefresh} />
          {children}
          <PdFooter hideChangeLang={hideChangeLang} />
        </MainContainerProvider>
      </main>
    </div>
  )
}

export default PageLayout
